"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Card, Button } from "../../../components/ui";
import { Toaster } from "react-hot-toast";
import toast from "react-hot-toast";
import Navbar from "../../../components/Navbar";
import { Shield, Mail, Phone, Lock, User, CheckCircle, ArrowRight, ChevronDown } from "lucide-react";

export default function TeacherRegisterPage() {
    const router = useRouter();
    const [step, setStep] = useState(1); // 1: Select Type/Send OTP, 2: Verify OTP, 3: Full Form
    const [submitting, setSubmitting] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [colleges, setColleges] = useState([]);
    const [loadingColleges, setLoadingColleges] = useState(false);
    const [otpType, setOtpType] = useState("phone"); // "phone" or "email"

    const [form, setForm] = useState({
        full_name: "",
        dob: "",
        age: "",
        gender: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        pincode: "",
        country_code: "+91",
        mobile: "",
        email: "",
        username: "",
        pwd: "",
        confirm_pwd: "",
        education: "",
        subject: "",
        experience_year: "0",
        experience_month: "0",
        organization: "",
        organization_id: "",
        department: "",
        // SmartCookie fields
        memberId: "",
        passw: "",
        ent_type: "103", // Default to Teacher
        otp: "",
    });

    // Fetch colleges
    useEffect(() => {
        const fetchColleges = async () => {
            setLoadingColleges(true);
            try {
                const res = await fetch("/api/register/colleges-dropdown");
                if (res.ok) {
                    const data = await res.json();
                    setColleges(data);
                }
            } catch (err) {
                console.error("Failed to fetch colleges:", err);
            } finally {
                setLoadingColleges(false);
            }
        };
        fetchColleges();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => {
            const newForm = { ...prev, [name]: value };

            // Auto-calculate age if DOB changes
            if (name === "dob" && value) {
                const birthDate = new Date(value);
                const today = new Date();
                let age = today.getFullYear() - birthDate.getFullYear();
                const m = today.getMonth() - birthDate.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                    age--;
                }
                newForm.age = age.toString() + " Years";
            }

            // Handle Organization selection
            if (name === "organization") {
                if (value === "Other") {
                    newForm.organization_id = "open";
                } else {
                    const selectedCollege = colleges.find((c) => c.college_name === value);
                    if (selectedCollege) {
                        newForm.organization_id = selectedCollege.application_number;
                    }
                }
            }

            return newForm;
        });
    };

    const handleSendOTP = async () => {
        if (otpType === "phone" && !form.mobile) {
            toast.error("Please enter mobile number");
            return;
        }
        if (otpType === "email" && !form.email) {
            toast.error("Please enter email address");
            return;
        }

        setOtpLoading(true);
        try {
            const payload = {
                operation: "send_otp",
                country_code: otpType === "phone" ? form.country_code : "",
                phone_number: otpType === "phone" ? form.mobile : "",
                email_id: otpType === "email" ? form.email : "",
                msg: "STAR_OTP"
            };

            const res = await fetch("/api/student/register", {
                method: "POST",
                body: JSON.stringify(payload),
            });

            const data = await res.json();
            if (data.responseStatus === 200) {
                toast.success("OTP sent successfully!");
                setStep(2);
            } else {
                toast.error(data.responseMessage || "Failed to send OTP");
            }
        } catch (err) {
            toast.error("Error sending OTP");
        } finally {
            setOtpLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        if (!form.otp) {
            toast.error("Please enter OTP");
            return;
        }

        setOtpLoading(true);
        try {
            const payload = {
                country_code: otpType === "phone" ? form.country_code : null,
                phone_number: otpType === "phone" ? form.mobile : null,
                email_id: otpType === "email" ? form.email : null,
                otp: form.otp
            };

            const res = await fetch("/api/student/verify-otp", {
                method: "POST",
                body: JSON.stringify(payload),
            });

            const data = await res.json();
            if (data.responseStatus === 200) {
                toast.success("OTP verified successfully!");
                setStep(3);
            } else {
                toast.error(data.responseMessage || "Invalid OTP");
            }
        } catch (err) {
            toast.error("Error verifying OTP");
        } finally {
            setOtpLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (form.pwd !== form.confirm_pwd) {
            toast.error("Passwords do not match!");
            return;
        }

        setSubmitting(true);
        try {
            const formData = new FormData();
            Object.keys(form).forEach((key) => {
                if (key !== "otp") formData.append(key, form[key]);
            });

            const res = await fetch("/api/teacher/register", {
                method: "POST",
                body: formData,
            });

            const data = await res.json();

            if (data.responseStatus === 200) {
                toast.success("Teacher registration successful!");
                setTimeout(() => {
                    router.push("/staff/login");
                }, 2000);
            } else {
                toast.error(data.responseMessage || "Registration failed");
            }
        } catch (err) {
            console.error("Registration error:", err);
            toast.error("An error occurred during registration");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <Navbar />
            <Toaster position="top-right" />

            <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-slate-900">Teacher Registration</h1>
                        <p className="text-slate-600 mt-2">Join our professional network</p>

                        {/* Step Indicator */}
                        <div className="flex items-center justify-center mt-6 space-x-4">
                            {[1, 2, 3].map((s) => (
                                <div key={s} className="flex items-center">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step === s ? "bg-blue-600 text-white shadow-lg shadow-blue-200" :
                                        step > s ? "bg-green-500 text-white" : "bg-slate-200 text-slate-500"
                                        }`}>
                                        {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                                    </div>
                                    {s < 3 && <div className={`w-12 h-1 bg-slate-200 mx-2 ${step > s ? "bg-green-500" : ""}`} />}
                                </div>
                            ))}
                        </div>
                    </div>

                    <Card className="p-8 border border-slate-200 shadow-sm bg-white rounded-2xl">
                        <AnimatePresence mode="wait">
                            {/* STEP 1: SEND OTP */}
                            {step === 1 && (
                                <motion.div
                                    key="step1"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    className="space-y-6"
                                >
                                    <div className="bg-blue-50 p-4 rounded-xl text-blue-800 text-sm mb-6">
                                        Please select your preferred method for OTP verification.
                                    </div>

                                    <div className="flex space-x-6 mb-8 justify-center">
                                        <label className="flex items-center space-x-2 cursor-pointer group">
                                            <input
                                                type="radio"
                                                name="otpType"
                                                checked={otpType === "phone"}
                                                onChange={() => setOtpType("phone")}
                                                className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                                            />
                                            <span className={`text-sm font-medium ${otpType === "phone" ? "text-blue-700" : "text-slate-600"}`}>Mobile Number</span>
                                        </label>
                                        <label className="flex items-center space-x-2 cursor-pointer group">
                                            <input
                                                type="radio"
                                                name="otpType"
                                                checked={otpType === "email"}
                                                onChange={() => setOtpType("email")}
                                                className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                                            />
                                            <span className={`text-sm font-medium ${otpType === "email" ? "text-blue-700" : "text-slate-600"}`}>Email ID</span>
                                        </label>
                                    </div>

                                    {otpType === "phone" ? (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="relative">
                                                <label className="block text-sm font-medium text-slate-700 mb-1.5">Country Code</label>
                                                <select
                                                    name="country_code"
                                                    value={form.country_code}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 bg-white appearance-none transition-all"
                                                >
                                                    <option value="+91">+91 (India)</option>
                                                    <option value="+1">+1 (USA)</option>
                                                </select>
                                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium text-slate-700 mb-1.5">Mobile Number</label>
                                                <input
                                                    type="tel"
                                                    name="mobile"
                                                    placeholder="Your 10 digit number"
                                                    value={form.mobile}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 transition-all"
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="w-full">
                                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="yourname@example.com"
                                                value={form.email}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 transition-all"
                                            />
                                        </div>
                                    )}

                                    <div className="flex justify-center pt-6">
                                        <Button
                                            onClick={handleSendOTP}
                                            loading={otpLoading}
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-4 rounded-xl font-bold shadow-xl shadow-blue-100 flex items-center"
                                        >
                                            Send Verification Code <ArrowRight className="ml-2 w-5 h-5" />
                                        </Button>
                                    </div>
                                </motion.div>
                            )}

                            {/* STEP 2: VERIFY OTP */}
                            {step === 2 && (
                                <motion.div
                                    key="step2"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    className="space-y-6 text-center"
                                >
                                    <div className="bg-slate-50 p-6 rounded-2xl inline-block mb-4">
                                        <Shield className="w-12 h-12 text-blue-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-slate-800">Verify OTP</h2>
                                        <p className="text-slate-500 mt-1">Enter the code sent to {otpType === "phone" ? form.mobile : form.email}</p>
                                    </div>

                                    <div className="max-w-xs mx-auto">
                                        <input
                                            type="text"
                                            name="otp"
                                            value={form.otp}
                                            onChange={handleInputChange}
                                            placeholder="XXXX"
                                            maxLength={4}
                                            className="w-full text-center text-3xl tracking-[1em] px-4 py-4 border-2 border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                                        />
                                    </div>

                                    <div className="flex flex-col items-center space-y-4 pt-4">
                                        <Button
                                            onClick={handleVerifyOTP}
                                            loading={otpLoading}
                                            className="w-full max-w-xs bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold shadow-lg"
                                        >
                                            Verify Code
                                        </Button>
                                        <button
                                            onClick={() => setStep(1)}
                                            className="text-slate-400 hover:text-slate-600 text-sm font-medium"
                                        >
                                            Wrong details? Go back
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {/* STEP 3: FULL FORM */}
                            {step === 3 && (
                                <motion.div
                                    key="step3"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="space-y-10"
                                >
                                    {/* SmartCookie Rewards Section */}
                                    <section className="bg-slate-900 p-8 rounded-2xl text-white shadow-lg overflow-hidden relative">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full -mr-16 -mt-16 blur-3xl"></div>
                                        <div className="flex items-center space-x-4 mb-6 relative z-10">
                                            <div className="bg-blue-600/20 p-3 rounded-xl border border-blue-500/30">
                                                <Shield className="w-8 h-8 text-blue-400" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold">Connect to SmartCookie</h2>
                                                <p className="text-slate-400 text-sm">Verify your rewards account to continue</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
                                            <div className="space-y-1.5">
                                                <label className="text-xs uppercase tracking-wider font-bold text-slate-400">Member ID *</label>
                                                <input
                                                    required
                                                    name="memberId"
                                                    value={form.memberId}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-blue-500 focus:bg-white/10 outline-none transition-all placeholder:text-white/20"
                                                    placeholder="Your Member ID"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-xs uppercase tracking-wider font-bold text-slate-400">Password *</label>
                                                <input
                                                    required
                                                    type="password"
                                                    name="passw"
                                                    value={form.passw}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-blue-500 focus:bg-white/10 outline-none transition-all placeholder:text-white/20"
                                                    placeholder="••••••••"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-xs uppercase tracking-wider font-bold text-slate-400">User Type</label>
                                                <div className="relative">
                                                    <select
                                                        name="ent_type"
                                                        value={form.ent_type}
                                                        onChange={handleInputChange}
                                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-blue-500 focus:bg-white/10 outline-none transition-all appearance-none"
                                                    >
                                                        <option value="103" className="text-slate-900">Teacher</option>
                                                    </select>
                                                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                                                </div>
                                            </div>
                                        </div>
                                        <p className="mt-4 text-xs text-slate-500 italic text-center relative z-10">Don't have an account? <a href="https://smartcookie.in/core/express_registration_sp.php" target="_blank" className="text-blue-400 hover:text-blue-300 transition-colors font-bold">Register on SmartCookie platform</a></p>
                                    </section>

                                    <form onSubmit={handleSubmit} className="space-y-12">
                                        {/* Profile Info */}
                                        <section>
                                            <h3 className="text-lg font-bold text-slate-800 flex items-center mb-6">
                                                <User className="w-5 h-5 mr-2 text-blue-600" /> Personal Information
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Full Name *</label>
                                                    <input required name="full_name" value={form.full_name} onChange={handleInputChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 outline-none transition-all" placeholder="Legal name" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Gender *</label>
                                                    <div className="relative">
                                                        <select required name="gender" value={form.gender} onChange={handleInputChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 outline-none appearance-none transition-all bg-white">
                                                            <option value="">Select Gender</option>
                                                            <option value="male">Male</option>
                                                            <option value="female">Female</option>
                                                        </select>
                                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                                    </div>
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Date of Birth</label>
                                                    <input type="date" name="dob" value={form.dob} onChange={handleInputChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 outline-none transition-all" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Calculated Age</label>
                                                    <input disabled value={form.age} className="w-full px-4 py-3 border border-slate-100 rounded-xl bg-slate-50 text-slate-500 font-medium cursor-not-allowed" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Mobile (Verified)</label>
                                                    <input disabled value={form.mobile} className="w-full px-4 py-3 border border-slate-100 rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Email (Verified)</label>
                                                    <input disabled value={form.email} className="w-full px-4 py-3 border border-slate-100 rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed" />
                                                </div>
                                            </div>
                                        </section>

                                        {/* Professional Info */}
                                        <section>
                                            <h3 className="text-lg font-bold text-slate-800 flex items-center mb-6">
                                                <Shield className="w-5 h-5 mr-2 text-blue-600" /> Professional Details
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="md:col-span-2 space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Organization / College *</label>
                                                    <div className="relative">
                                                        <select required name="organization" value={form.organization} onChange={handleInputChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 outline-none appearance-none transition-all bg-white">
                                                            <option value="">Select Organization</option>
                                                            {colleges.map((c) => (
                                                                <option key={c.application_number} value={c.college_name}>{c.college_name}</option>
                                                            ))}
                                                            <option value="Other">Other (Manually Specify)</option>
                                                        </select>
                                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                                    </div>
                                                </div>
                                                {form.organization_id === "open" && (
                                                    <div className="md:col-span-2 space-y-1 animate-in fade-in slide-in-from-top-2">
                                                        <input required name="organization_manual" onChange={(e) => setForm(f => ({ ...f, organization: e.target.value }))} className="w-full px-4 py-3 border-2 border-blue-100 rounded-xl bg-blue-50 outline-none focus:ring-4 focus:ring-blue-100/50 transition-all" placeholder="Enter full organization name" />
                                                    </div>
                                                )}
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Education</label>
                                                    <input name="education" value={form.education} onChange={handleInputChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 outline-none transition-all" placeholder="e.g. M.Tech, PhD" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Department</label>
                                                    <input name="department" value={form.department} onChange={handleInputChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 outline-none transition-all" placeholder="e.g. Computer Science" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Experience (Years)</label>
                                                    <input type="number" name="experience_year" value={form.experience_year} onChange={handleInputChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 outline-none transition-all" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Experience (Months)</label>
                                                    <input type="number" name="experience_month" value={form.experience_month} onChange={handleInputChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 outline-none transition-all" />
                                                </div>
                                            </div>
                                        </section>

                                        {/* Login Security */}
                                        <section>
                                            <h3 className="text-lg font-bold text-slate-800 flex items-center mb-6">
                                                <Lock className="w-5 h-5 mr-2 text-blue-600" /> Login Credentials
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Choose Username *</label>
                                                    <input required name="username" value={form.username} onChange={handleInputChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 outline-none transition-all" placeholder="Unique username" />
                                                </div>
                                                <div></div>
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Password *</label>
                                                    <input required type="password" name="pwd" value={form.pwd} onChange={handleInputChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 outline-none transition-all" placeholder="7-12 characters" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-semibold text-slate-600">Confirm Password *</label>
                                                    <input required type="password" name="confirm_pwd" value={form.confirm_pwd} onChange={handleInputChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50/50 outline-none transition-all" placeholder="Repeat password" />
                                                </div>
                                            </div>
                                        </section>

                                        <div className="pt-8 border-t flex items-center justify-between">
                                            <button type="button" onClick={() => setStep(2)} className="text-slate-400 font-bold hover:text-slate-600 transition-colors px-4 py-2">Back to OTP</button>
                                            <Button
                                                type="submit"
                                                loading={submitting}
                                                className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-4 rounded-xl font-bold shadow-md hover:shadow-lg transition-all"
                                            >
                                                Finish Registration
                                            </Button>
                                        </div>
                                    </form>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Card>
                </motion.div>
            </main>
        </div>
    );
}
